(() => {
  "use strict";

  const STORAGE_KEY = "ecpr:project-review:v3:attribution";
  const PROJECT_REVIEW_PATH = "/pages/project-review";
  const INTERNAL_ENTRY_POINTS = new Set(["homepage_process", "direct_project_review"]);
  const QUERY_FIELDS = {
    utm_source: "utm_source",
    utm_medium: "utm_medium",
    utm_campaign: "utm_campaign",
    utm_content: "utm_content",
    utm_term: "utm_term",
    gclid: "gclid",
    gbraid: "gbraid",
    wbraid: "wbraid",
    dclid: "dclid",
    fbclid: "fbclid",
    msclkid: "msclkid",
    ttclid: "ttclid",
  };

  const normalize = (value) => {
    try {
      return String(value ?? "").normalize("NFKC");
    } catch (_error) {
      return String(value ?? "");
    }
  };

  const sanitizeText = (value, maxLength = 160) =>
    normalize(value)
      .replace(/[\u0000-\u001f\u007f]/g, "")
      .replace(/[^A-Za-z0-9 _.,:/+@~\-]/g, "")
      .trim()
      .slice(0, maxLength);

  const sanitizeToken = (value, maxLength = 180) =>
    normalize(value)
      .replace(/[^A-Za-z0-9._~:+/\-]/g, "")
      .slice(0, maxLength);

  const sanitizePath = (value) => {
    const path = normalize(value)
      .split(/[?#]/, 1)[0]
      .replace(/[^A-Za-z0-9._~/%\-]/g, "")
      .slice(0, 300);
    return path.startsWith("/") ? path : `/${path}`;
  };

  const readStoredAttribution = () => {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch (_error) {
      return null;
    }
  };

  const persistAttribution = (attribution) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch (_error) {
      // The page-only script can still use the in-memory value when storage is unavailable.
    }
  };

  const isStoreHost = (hostname) => {
    const host = String(hostname || "").toLowerCase();
    return (
      host === window.location.hostname.toLowerCase() ||
      host === "eurocustomspr.com" ||
      host === "www.eurocustomspr.com" ||
      host === "eurocustomspr.myshopify.com" ||
      host.endsWith(".eurocustomspr.com")
    );
  };

  const externalReferrerHostPath = () => {
    if (!document.referrer) return "";
    try {
      const referrer = new URL(document.referrer);
      if (!/^https?:$/.test(referrer.protocol) || isStoreHost(referrer.hostname)) return "";
      const host = sanitizeToken(referrer.hostname.toLowerCase(), 180);
      return `${host}${sanitizePath(referrer.pathname || "/")}`.slice(0, 480);
    } catch (_error) {
      return "";
    }
  };

  const queryParameters = () => {
    try {
      return new URLSearchParams(window.location.search);
    } catch (_error) {
      return new URLSearchParams();
    }
  };

  const internalEntryPoint = (parameters) => {
    const value = sanitizeToken(parameters.get("entry") || "", 80);
    if (INTERNAL_ENTRY_POINTS.has(value)) return value;
    return window.location.pathname === PROJECT_REVIEW_PATH ? "direct_project_review" : "";
  };

  const captureFirstTouch = () => {
    const stored = readStoredAttribution();
    if (stored) {
      if (!stored.entry_point && (window.location.pathname === PROJECT_REVIEW_PATH || window.location.search.includes("entry="))) {
        const entryPoint = internalEntryPoint(queryParameters());
        if (entryPoint) {
          stored.entry_point = entryPoint;
          persistAttribution(stored);
        }
      }
      return stored;
    }

    const parameters = queryParameters();
    const attribution = {
      entry_point: internalEntryPoint(parameters),
      initial_landing_path: sanitizePath(window.location.pathname || "/"),
      external_referrer_host_path: externalReferrerHostPath(),
    };

    Object.entries(QUERY_FIELDS).forEach(([storageKey, queryKey]) => {
      const sanitizer = storageKey.includes("clid") || storageKey.includes("braid") ? sanitizeToken : sanitizeText;
      attribution[storageKey] = sanitizer(parameters.get(queryKey) || "", 180);
    });

    persistAttribution(attribution);
    return attribution;
  };

  window.ECPRProjectReviewAttribution = captureFirstTouch();
})();
